import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayHoursPublicSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API_BASE}/payments/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
        } else if (attempts < 12) {
          setTimeout(poll, 2500);
        } else {
          setState({ status: "pending", data });
        }
      } catch {
        if (attempts < 6) {
          setTimeout(poll, 3000);
        } else if (!cancelled) {
          setState({ status: "error", data: null });
        }
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Card className="rounded-sm border-zinc-200 bg-white p-8 max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-6 text-brand">
          <BrandLogo size="lg" />
          <div className="font-display text-lg font-bold tracking-tighter text-zinc-950">Wineemotion</div>
        </div>
        {state.status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 text-brand animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">Verifica in corso...</h2>
            <p className="text-sm text-zinc-500 mt-2">Attendere: stiamo confermando il pagamento con Stripe.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">Pagamento confermato</h2>
            <p className="text-sm text-zinc-600 mt-3">
              Le ore di assistenza sono state accreditate. Il nostro tecnico può ora procedere con l&apos;intervento.
            </p>
            <p className="text-xs text-zinc-400 mt-4">Riceverai anche una email di conferma con la ricevuta.</p>
            <p className="text-xs text-zinc-400 mt-6">Puoi chiudere questa pagina.</p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">Elaborazione in corso</h2>
            <p className="text-sm text-zinc-500 mt-2">
              Il pagamento sta ancora venendo processato da Stripe. Riceverai una notifica appena confermato.
            </p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">Errore verifica pagamento</h2>
            <p className="text-sm text-zinc-500 mt-2">Contatta l&apos;assistenza indicando questo codice: {sessionId?.slice(-12)}</p>
            <Link to="/" className="inline-block mt-4 text-xs uppercase tracking-widest text-brand">
              Torna alla home
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
